/**
 * URL Generation Utilities for Portfolio Creator
 * Ensures consistent URL generation across all components
 */

/**
 * Generate a clean published URL for a website
 * @param {string} username - The website owner's username
 * @param {string} domain - The website domain
 * @param {string} [subdomain] - Optional subdomain/page
 * @returns {string} Clean published URL without version slugs
 */
export const generatePublishedUrl = (username, domain, subdomain) => {
  let url = `/${username}/${domain}`;
  if (subdomain) {
    url += `/${subdomain}`;
  }
  return url;
};

/**
 * Generate a version-specific edit URL for a website
 * @param {string} username - The website owner's username
 * @param {string} domain - The website domain
 * @param {string} versionSlug - The version slug (must start with "draft-")
 * @param {string} [subdomain] - Optional subdomain/page
 * @returns {string} Version-specific edit URL
 */
export const generateEditUrl = (username, domain, versionSlug, subdomain) => {
  let url = `/edit/${username}/${domain}/${versionSlug}`;
  if (subdomain) {
    url += `/${subdomain}`;
  }
  return url;
};

/**
 * Generate a dashboard URL for a user
 * @param {string} username - The user's username
 * @returns {string} Dashboard URL
 */
export const generateDashboardUrl = (username) => {
  return `/edit/${username}`;
};

/**
 * Build a target page URL from a link configuration (used in Button components)
 * @param {Object} item - The item containing link configuration
 * @returns {string} Target page URL
 */
export const buildTargetPageUrl = (item) => {
  const LINK_CHOICES = {
    INTERNAL: "internal",
    EXTERNAL: "external",
  };

  const linkChoice = (item?.link_choice || "").toLowerCase();
  const isInternal = linkChoice === LINK_CHOICES.INTERNAL;

  const normalizeSubdomain = (raw) => {
    if (raw === null || raw === undefined) return undefined;
    if (typeof raw !== "string") return undefined;
    const trimmed = raw.trim();
    if (!trimmed) return "";
    return trimmed.startsWith("/") ? trimmed.replace(/^\/+/, "") : trimmed;
  };

  const getRouteFallback = () => {
    try {
      if (typeof window === "undefined") return {};
      const pathname = window.location?.pathname || "";
      if (!pathname) return {};
      return extractRouteParams(pathname) || {};
    } catch (e) {
      return {};
    }
  };

  if (isInternal) {
    const linkInternal = item?.link_internal;
    const route = getRouteFallback();
    const fallbackUsername = route?.username;
    const fallbackDomain = route?.domain;

    // String shorthand: treat as subdomain
    if (typeof linkInternal === "string") {
      const sub = normalizeSubdomain(linkInternal);
      if (fallbackUsername && fallbackDomain) {
        return generatePublishedUrl(fallbackUsername, fallbackDomain, sub);
      }
    }

    if (linkInternal && typeof linkInternal === "object") {
      // Accept both backend WebsitePageRelationField shape and editor WebsitePageSerializer shape.
      const sub = normalizeSubdomain(linkInternal.subdomain);
      const targetDomain = linkInternal.domain || linkInternal.website_domain || fallbackDomain;
      const targetUsername = linkInternal.owner_username || fallbackUsername;

      if (targetUsername && targetDomain) {
        return generatePublishedUrl(targetUsername, targetDomain, sub);
      }

      // If we at least have a subdomain, try using current route context.
      if (fallbackUsername && fallbackDomain && sub !== undefined) {
        return generatePublishedUrl(fallbackUsername, fallbackDomain, sub);
      }
    }
  }

  // Fallback to external URL (or no-op) if internal is not resolvable.
  return item?.link_external || "#";
};

/**
 * Validate if a version slug is properly formatted
 * @param {string} versionSlug - The version slug to validate
 * @returns {boolean} Whether the version slug is valid
 */
export const isValidVersionSlug = (versionSlug) => {
  return typeof versionSlug === 'string' && versionSlug.startsWith('draft-');
};

/**
 * Extract route parameters from the current URL
 * @param {string} pathname - The current pathname
 * @returns {Object} Extracted route parameters
 */
export const extractRouteParams = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  
  if (segments[0] === 'edit') {
    // Edit URL format: /edit/username/domain/version_slug/subdomain?
    return {
      isEdit: true,
      username: segments[1],
      domain: segments[2],
      versionSlug: segments[3],
      subdomain: segments[4],
    };
  } else {
    // Published URL format: /username/domain/subdomain?
    return {
      isEdit: false,
      username: segments[0],
      domain: segments[1],
      subdomain: segments[2],
    };
  }
}; 